import { SaveFilled } from "@ant-design/icons"
import { Button, Flex, Form, Modal, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import dayjs from "dayjs"
import React, { useState } from "react"

import Icon from "../../../../Icon"
import ReactRouterPrompt from "../../../../components/ReactRouterPrompt.tsx"
import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../../components/main-content.ts"
import { type TeamMembersFilterOptions } from "../../../TeamManagementPage/redux/team-members.types.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"
import { SettingsCompanyFormChangedWatcher } from "./SettingsCompanyFormChangedWatcher.tsx"
import { CompanyBrandingFormSection } from "./sections/CompanyBrandingFormSection.tsx"
import { CompanyInformationFormSection } from "./sections/CompanyInformationFormSection.tsx"

export interface SettingsCompanyFormData {
  business_name: string
  company_phone: string
  license_number: string
  bond_number?: string
  ein?: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
  general_liability?: string
  logo?: string | File | null
  insurance_number?: string
  lead_cost_method?: string
  company_website?: string
  company_description?: string
  receiving_account: string
  company_email?: string
  default_tax_pct?: string | number | null
  default_retainage_pct?: string | number | null
  cash_flow_threshold_percent?: string | number | null
  contract_tolerance_percent?: string | number | null
}

interface SettingsCompanyFormProps {
  form: FormInstance<SettingsCompanyFormData>
  initialValues?: SettingsCompanyFormData
  options: TeamMembersFilterOptions | null
  readonly?: boolean
  saving: boolean
  onFinish: (data: SettingsCompanyFormData) => void
  onReset: () => void
}

export function SettingsCompanyForm({
  form,
  options,
  readonly,
  initialValues,
  saving,
  onReset,
  onFinish
}: SettingsCompanyFormProps) {
  const [isDirty, setIsDirty] = useState(false)

  return (
    <>
      <ReactRouterPrompt when={isDirty}>
        {({ isActive, onConfirm, onCancel }) => (
          <Modal
            open={isActive}
            title="Are you sure you want to leave?"
            onOk={onConfirm}
            onCancel={onCancel}
            okText="Leave"
            cancelText="Stay"
          >
            <p>You have unsaved changes. If you leave, they will be lost.</p>
          </Modal>
        )}
      </ReactRouterPrompt>

      <Form
        form={form}
        clearOnDestroy
        onFinish={onFinish}
        layout="vertical"
        className={"flex-1 flex gap-3 flex-col w-full with-default-error"}
        validateTrigger={["onSubmit", "onChange"]}
        disabled={saving || readonly}
        initialValues={initialValues}
      >
        <SettingsCompanyFormChangedWatcher
          form={form}
          initialValues={initialValues}
          setIsDirty={setIsDirty}
          saving={saving}
        />

        <div className="lg:columns-2 space-y-3 flex-1">
          <SettingsCard
            iconWrapperClassName="bg-blue-100"
            icon={<Icon type={"Office"} className="text-blue-600" />}
            title="Company Information"
          >
            <CompanyInformationFormSection form={form} options={options} />
          </SettingsCard>
          <SettingsCard
            iconWrapperClassName="bg-purple-100"
            icon={<Icon type={"Palette"} className="text-purple-600" />}
            title="Branding"
          >
            <CompanyBrandingFormSection
              form={form}
              initialValues={initialValues}
            />
          </SettingsCard>
        </div>
        <Flex
          vertical
          gap={"small"}
          justify={"space-between"}
          className={`lg:flex-row max-lg:items-center ${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[2] bg-background py-2`}
        >
          <span />

          {/*<Flex*/}
          {/*  gap={"small"}*/}
          {/*  align={"center"}*/}
          {/*  className={"max-lg:w-full max-lg:justify-between "}*/}
          {/*>*/}
          {/*  <Button disabled>Reset to Defaults</Button>*/}
          {/*  <Typography.Text type={"secondary"}>*/}
          {/*    Last saved: Today at {dayjs().format("h:mm A")}*/}
          {/*  </Typography.Text>*/}
          {/*</Flex>*/}

          <Flex
            gap={"small"}
            className={"max-lg:w-full max-lg:justify-between "}
          >
            <Button disabled={!isDirty || saving} onClick={onReset}>
              Cancel Changes
            </Button>
            <Button
              htmlType="submit"
              disabled={!isDirty}
              loading={saving}
              icon={<SaveFilled />}
              type={"primary"}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Form>
    </>
  )
}
